import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import { isNobleChallenge } from '@/utils/badges'

export const useDataStore = defineStore('data', () => {
// State
const ascents = ref([])
const activities = ref([])
const achievements = ref([])
const challenges = ref([])
const passport = ref(null) // Paszport krajów (GET /api/passport)
const adminLocationStats = ref([])
const loading = ref(false)
const error = ref(null)

// Activities pagination state
const activitiesPagination = ref({
  currentOffset: 0,
  pageSize: 20,
  hasMore: true,
  loadingMore: false,
  totals: null,
  filters: {}
})  // Getters
  const totalAscents = computed(() => ascents.value.length)

  const uniqueLocations = computed(() => {
    const locations = new Set(ascents.value.map(ascent => ascent.location_id))
    return locations.size
  })

  const highestPeak = computed(() => {
    if (ascents.value.length === 0) return 0
    return Math.max(...ascents.value.map(ascent => ascent.location_elevation || 0))
  })

  // Actions
  const loadAscents = async (force = false) => {
    if (!force && ascents.value.length > 0) return ascents.value // Return cached data

    try {
      loading.value = true
      error.value = null

      const response = await api.get('/api/ascents')
      ascents.value = response.data.locations || []

      console.log('Loaded ascents:', ascents.value.length)
      return ascents.value
    } catch (err) {
      console.error('Error loading ascents:', err)
      error.value = 'Failed to load ascents data'
      throw err
    } finally {
      loading.value = false
    }
  }

  const loadActivities = async (force = false, loadAll = false) => {
    if (!force && activities.value.length > 0 && !loadAll) return activities.value // Return cached data

    try {
      loading.value = true
      error.value = null

      let params = {}
      if (loadAll) {
        // Load all activities by setting a very high limit
        params.limit = 10000 // Assuming no user has more than 10k activities
        params.offset = 0
      }

      const response = await api.get('/api/activities', { params })
      activities.value = response.data.activities || []

      // Store totals if provided (when offset=0)
      if (response.data.totals) {
        activitiesPagination.value.totals = response.data.totals
      }

      console.log('Loaded activities:', activities.value.length)
      return activities.value
    } catch (err) {
      console.error('Error loading activities:', err)
      error.value = 'Failed to load activities data'
      throw err
    } finally {
      loading.value = false
    }
  }

  const loadActivitiesPaginated = async (reset = true, filters = {}) => {
    try {
      if (reset) {
        activitiesPagination.value.loadingMore = false
        activitiesPagination.value.currentOffset = 0
        activitiesPagination.value.hasMore = true
        activitiesPagination.value.totals = null
        activities.value = []
      } else {
        activitiesPagination.value.loadingMore = true
      }

      activitiesPagination.value.filters = filters

      const params = {
        limit: activitiesPagination.value.pageSize,
        offset: activitiesPagination.value.currentOffset,
        ...filters
      }

      const response = await api.get('/api/activities', { params })
      const newActivities = response.data.activities || []

      if (newActivities.length < activitiesPagination.value.pageSize) {
        activitiesPagination.value.hasMore = false
      }

      if (reset) {
        activities.value = newActivities
      } else {
        activities.value = [...activities.value, ...newActivities]
      }

      activitiesPagination.value.currentOffset += activitiesPagination.value.pageSize

      // Store totals if provided (only on first page)
      if (response.data.totals) {
        activitiesPagination.value.totals = response.data.totals
      }

      return activities.value
    } catch (err) {
      console.error('Error loading activities:', err)
      error.value = 'Failed to load activities data'
      throw err
    } finally {
      activitiesPagination.value.loadingMore = false
      if (reset) {
        loading.value = false
      }
    }
  }

  const loadAchievements = async (force = false) => {
    if (!force && achievements.value.length > 0) return achievements.value // Return cached data

    try {
      loading.value = true
      error.value = null

      const response = await api.get('/api/achievment')
      achievements.value = response.data.achievments || []

      console.log('Loaded achievements:', achievements.value.length)
      return achievements.value
    } catch (err) {
      console.error('Error loading achievements:', err)
      error.value = 'Failed to load achievements data'
      throw err
    } finally {
      loading.value = false
    }
  }

  const loadChallenges = async (force = false) => { // eslint-disable-line no-unused-vars
    // Nie cache'uj wyzwań - postęp może się zmieniać
    try {
      loading.value = true
      error.value = null

      // Get challenges from new challenges endpoint
      const response = await api.get('/api/challenges')
      const challengesData = response.data.challenges || []

      // Transform challenges into UI format
      challenges.value = challengesData.map(challenge => {
        const userProgress = challenge.user_progress || {}
        const progress = userProgress.progress || 0
        const completed = userProgress.completed || false
        const status = completed ? 'completed' : progress > 0 ? 'in-progress' : 'not-started'
        const statusText = completed ? 'Completed' : status === 'in-progress' ? 'In Progress' : 'Not Started'

        return {
          id: challenge.id,
          name: challenge.name,
          title: challenge.name, // for backward compatibility
          description: challenge.description,
          icon: challenge.icon || '🏔️',
          category: challenge.category || 'general',
          status: status,
          statusText: statusText,
          progress: progress,
          level: userProgress.level,
          userProgress: userProgress.details || {},
          conditions: challenge.conditions,
          levels: challenge.levels,
          requires_challenges: challenge.requires_challenges || [],
          // Szczyty wymagane przez wyzwanie (nazwa, wysokość, współrzędne) — bez pobierania katalogu
          peaks: Array.isArray(challenge.peaks) ? challenge.peaks : null,
          noble: isNobleChallenge(challenge) // flaga "rzadka" (wariant szlachetny)
        }
      })

      console.log('Loaded challenges:', challenges.value.length)
      return challenges.value
    } catch (err) {
      console.error('Error loading challenges:', err)
      error.value = 'Failed to load challenges data'
      throw err
    } finally {
      loading.value = false
    }
  }

  const checkChallenges = async () => {
    try {
      loading.value = true
      error.value = null

      // Call POST /api/challenges to check and update user progress
      const response = await api.post('/api/challenges')
      const result = response.data

      console.log('Challenges checked:', result.updated, 'updated')

      // Reload challenges to get updated progress
      await loadChallenges(true) // force reload

      return result
    } catch (err) {
      console.error('Error checking challenges:', err)
      error.value = 'Failed to check challenges'
      throw err
    } finally {
      loading.value = false
    }
  }

  const loadAdminLocationStats = async (force = false) => {
    if (!force && adminLocationStats.value.length > 0) return adminLocationStats.value

    try {
      loading.value = true
      error.value = null
      const response = await api.get('/api/admin/locations/stats')
      adminLocationStats.value = response.data.locations || []
      return adminLocationStats.value
    } catch (err) {
      console.error('Error loading admin location stats:', err)
      error.value = 'Failed to load admin location stats'
      throw err
    } finally {
      loading.value = false
    }
  }

  const loadAllData = async () => {
    try {
      loading.value = true
      error.value = null

      // Load all data in parallel
      const [ascentsData, activitiesData, achievementsData, challengesData] = await Promise.allSettled([
        loadAscents(),
        loadActivities(true, false), // Load first page to get totals
        loadAchievements(),
        loadChallenges()
      ])

      // Log results
      if (ascentsData.status === 'fulfilled') {
        console.log('Ascents loaded successfully')
      } else {
        console.error('Failed to load ascents:', ascentsData.reason)
      }

      if (activitiesData.status === 'fulfilled') {
        console.log('Activities loaded successfully')
      } else {
        console.error('Failed to load activities:', activitiesData.reason)
      }

      if (achievementsData.status === 'fulfilled') {
        console.log('Achievements loaded successfully')
      } else {
        console.error('Failed to load achievements:', achievementsData.reason)
      }

      if (challengesData.status === 'fulfilled') {
        console.log('Challenges loaded successfully')
      } else {
        console.error('Failed to load challenges:', challengesData.reason)
      }

    } catch (err) {
      console.error('Error loading data:', err)
      error.value = 'Failed to load application data'
    } finally {
      loading.value = false
    }
  }

  // Paszport krajów: odznaczenia liczone przez BE z wejść użytkownika
  const loadPassport = async () => {
    const response = await api.get('/api/passport')
    passport.value = response.data
    return passport.value
  }

  const clearData = () => {
    passport.value = null
    ascents.value = []
    activities.value = []
    achievements.value = []
    challenges.value = []
    error.value = null
  }

  // Activities pagination getters
  const activitiesLoading = computed(() => loading.value || activitiesPagination.value.loadingMore)
  const activitiesHasMore = computed(() => activitiesPagination.value.hasMore)
  const activitiesTotals = computed(() => activitiesPagination.value.totals)

  return {
    // State
    ascents,
    activities,
    achievements,
    challenges,
    passport,
    adminLocationStats,
    loading,
    error,
    activitiesPagination,

    // Getters
    totalAscents,
    uniqueLocations,
    highestPeak,
    activitiesLoading,
    activitiesHasMore,
    activitiesTotals,

    // Actions
    loadAscents,
    loadActivities,
    loadActivitiesPaginated,
    loadAchievements,
    loadChallenges,
    checkChallenges,
    loadPassport,
    loadAdminLocationStats,
    loadAllData,
    clearData
  }
})